import { useCallback, useEffect, useState } from "react";
import { getTeamMembers } from "../api/members";
import type { TeamMember, TeamMembersPage } from "../types";

interface UseTeamMembers extends TeamMembersPage {
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  /** Mutate one member in place — avoids a full refetch after a single
   *  field change (e.g. role update) which would reset scroll position
   *  and flash a loading state across the whole table. */
  patchMember: (id: TeamMember["id"], partial: Partial<TeamMember>) => void;
}

export function useTeamMembers(page: number, size: number): UseTeamMembers {
  const [data, setData] = useState<TeamMembersPage>({ total: 0, members: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  const patchMember = useCallback(
    (id: TeamMember["id"], partial: Partial<TeamMember>) => {
      setData((prev) => ({
        ...prev,
        members: prev.members.map((m) =>
          String(m.id) === String(id) ? { ...m, ...partial } : m,
        ),
      }));
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await getTeamMembers({ page, size }, { signal: controller.signal });
        setData(res);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [page, size, tick]);

  return { ...data, loading, error, refresh, patchMember };
}
